import { captureException } from "@sentry/nextjs";
import type { WalletName } from "@solana/wallet-adapter-base";
import { useWallet } from "@solana/wallet-adapter-react";
import type { Wallet } from "@solana/wallet-adapter-react";
import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";

import { useSettingsPopup } from "contexts/SettingsPopupContext";
import { WalletConnectMode } from "tw/components/molecules/SettingsPopupContent/WalletConnectBody";

export const useWalletConnector = () => {
  const { connected, connecting, disconnect, select, wallet, wallets } =
    useWallet();
  const [requestConnect, setRequestConnect] = useState(false);

  const { openSettingsPopup: openDrawer, closeSettingsPopup: closeDrawer } =
    useSettingsPopup();

  const tryConnect = useCallback(async () => {
    if (wallet?.adapter) {
      try {
        await wallet.adapter.connect();
        setRequestConnect(false);
      } catch {
        await disconnect();
      }
    }
  }, [wallet?.adapter, disconnect]);

  useEffect(() => {
    if (
      wallet?.adapter &&
      wallet &&
      !connected &&
      !connecting &&
      requestConnect
    ) {
      tryConnect();
    }
  }, [
    tryConnect,
    wallet?.adapter,
    wallet,
    connected,
    connecting,
    requestConnect,
  ]);

  const openWalletDrawer = useCallback(
    (mode: WalletConnectMode) => {
      openDrawer(
        {
          title: t("walletConnect.title") ?? "",
        },
        {
          wallets,
          initMode: mode,
          requestConnection: (w: Wallet) => {
            closeDrawer();
            select(w.adapter.name);
            if (("Mobile Wallet Adapter" as WalletName) === w.adapter.name) {
              setRequestConnect(true);
            }
          },
        }
      );
    },
    [closeDrawer, openDrawer, select, wallets]
  );

  const connectWallet = useCallback(
    (previousWalletName: string | null) => {
      try {
        if (wallets.find((w) => w.adapter.name === previousWalletName)) {
          select(previousWalletName as WalletName);
          if (previousWalletName === "Mobile Wallet Adapter") {
            setRequestConnect(true);
          }
        } else {
          openWalletDrawer(WalletConnectMode.WalletSelection);
        }
      } catch (err) {
        captureException(err);
      }
    },
    [wallets, select, setRequestConnect, openWalletDrawer]
  );
  return { connectWallet, openWalletDrawer, tryConnect };
};
